import { ReactNode, Suspense, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { clearSignerSession, hasSignerSession } from '../auth/signer-session';
import {
    LayoutDashboard,
    FileText,
    ClipboardCheck,
    Users,
    UserSquare2,
    Activity,
    Calendar,
    ClipboardList,
    Settings,
    LayoutTemplate,
    LifeBuoy,
    Menu as MenuIcon,
    HelpCircle,
} from 'lucide-react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { CurrentUserProvider } from '../hooks/useCurrentUser';
import { useCurrentUser } from '../hooks/useCurrentUser.context';
import { useDisplayName } from '../hooks/useDisplayName';
import { useUserType } from '../hooks/useUserType';
import { EntitlementsProvider } from '../hooks/useEntitlements';
import { useEntitlements } from '../hooks/useEntitlements.context';
import { useGlobalHelpShortcut } from '../hooks/useGlobalHelpShortcut';
import { helpUrl } from '../lib/helpUrl';
import { recordError } from '../lib/rum';
import UserMenu from './UserMenu';
import LoadingPage from './LoadingPage';
import AccedoLogo from './AccedoLogo';

/** Sidebar width in pixels. */
const DRAWER_WIDTH = 240;

/**
 * Admin sidebar spacing (#2168). Tighter than the prior defaults so all
 * 9 nav buttons + 5 section labels + toolbar fit within 720px (a common
 * laptop viewport). The previous values (button py:1, label pt:1.5
 * pb:0.5 mt:1) pushed Settings to y≈693 with the bottom at y≈739 — past
 * the fold. Pure constants so the height-budget test can compute the
 * cumulative sidebar height deterministically.
 */
export const ADMIN_SIDEBAR_BUTTON_PY = 0.75;
export const ADMIN_SIDEBAR_LABEL_PT = 1;
export const ADMIN_SIDEBAR_LABEL_PB = 0.25;
export const ADMIN_SIDEBAR_LABEL_MT = 0.5;

interface AuthenticatedLayoutProps {
    children: ReactNode;
}

interface NavItem {
    label: string;
    to: string;
    icon: ReactNode;
}

interface NavSection {
    label: string;
    items: NavItem[];
}

/** Inner component that holds all hooks and the full layout. Only rendered after loading completes. */
function AuthenticatedLayoutInner({ children }: AuthenticatedLayoutProps) {
    const { email, userType: currentUserType } = useCurrentUser();
    const displayName = useDisplayName('User');
    const { isAdmin } = useUserType();
    const { features } = useEntitlements();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const muiTheme = useMuiTheme();
    const isDesktop = useMediaQuery(muiTheme.breakpoints.up('sm'));

    // Global ? (Shift+/) keyboard shortcut → opens /help in a new tab. Skips
    // when focus is on an input/textarea/contentEditable so typing a literal ?
    // doesn't fire the shortcut.
    useGlobalHelpShortcut();

    const handleOpenHelp = () => {
        window.open(helpUrl(''), '_blank', 'noopener,noreferrer');
    };

    // When features are still loading (null), default to showing groups (permissive)
    const groupsEnabled = features?.groupsEnabled !== false;
    // When features are still loading (null), default to showing activity log (permissive)
    const auditLogEnabled = features?.auditLogEnabled !== false;
    // When features are still loading (null), default to showing templates (permissive)
    const templatesEnabled = features?.templatesEnabled !== false;

    // Close mobile drawer on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleSignOut = async () => {
        try {
            // Capture BEFORE clearing: a signer has no reactive auth signal.
            const wasSigner = hasSignerSession();
            // Clear the self-issued signer session (#2955) regardless — an
            // email-only signer has no Cognito session, so signOut() alone would
            // leave the JWT in sessionStorage and keep them "signed in".
            clearSignerSession();
            await signOut();
            if (wasSigner) {
                // Signers authenticate only via the sessionStorage JWT (#2955);
                // Cognito authStatus is already 'unauthenticated' before AND after
                // signOut(), so its Hub 'signedOut' event is a same-value no-op that
                // triggers no re-render, and clearing sessionStorage triggers none
                // either. An explicit navigate is the ONLY thing that moves a signer
                // off the protected route (mirrors LoginByToken's sign-in navigate).
                navigate('/login', { replace: true });
            }
            // For Cognito users (admins/SSO) we deliberately do NOT navigate here:
            // signOut() → Hub 'signedOut' → authStatus flips to 'unauthenticated',
            // re-rendering ProtectedRoute so its declarative <Navigate to="/login"
            // replace> is the single source of truth for the redirect. A competing
            // imperative navigate here caused the #3264 dual-navigation URL strand.
        } catch (error) {
            recordError(error, { source: 'AuthenticatedLayout.signOut' });
        }
    };

    const isActiveRoute = (path: string) => location.pathname === path;

    const handleDrawerToggle = () => {
        setMobileOpen((prev) => !prev);
    };

    /** Admin navigation sections */
    const adminSections: NavSection[] = [
        {
            label: 'Personal',
            items: [{ label: 'My Policies', to: '/signer', icon: <ClipboardList size={18} aria-hidden="true" /> }],
        },
        {
            label: 'Compliance',
            items: [
                { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard size={18} aria-hidden="true" /> },
                { label: 'Policies', to: '/policies', icon: <FileText size={18} aria-hidden="true" /> },
                { label: 'Acknowledgments', to: '/acknowledgments', icon: <ClipboardCheck size={18} aria-hidden="true" /> },
                { label: 'Calendar', to: '/calendar', icon: <Calendar size={18} aria-hidden="true" /> },
            ],
        },
        {
            label: 'Organization',
            items: [
                { label: 'Users', to: '/users', icon: <Users size={18} aria-hidden="true" /> },
                ...(groupsEnabled ? [{ label: 'Groups', to: '/groups', icon: <UserSquare2 size={18} aria-hidden="true" /> }] : []),
                ...(templatesEnabled ? [{ label: 'Templates', to: '/templates', icon: <LayoutTemplate size={18} aria-hidden="true" /> }] : []),
            ],
        },
        ...(auditLogEnabled
            ? [
                  {
                      label: 'Audit',
                      items: [{ label: 'Activity Log', to: '/activity-log', icon: <Activity size={18} aria-hidden="true" /> }],
                  },
              ]
            : []),
        {
            label: 'Admin',
            // "Settings" in the main nav is admin-only and routes directly
            // to the Billing panel — the historical default and the most-
            // visited tab. After issue #2114 split personal vs workspace
            // settings, signers reach their own settings through the
            // user-menu's "Personal Settings" item instead.
            //
            // "Support" (#2303) is a new tenant admin entry into the support
            // ticket service. It sits above Settings so the visually heavier
            // Settings item stays anchored at the bottom of the Admin section.
            items: [
                { label: 'Support', to: '/support/tickets', icon: <LifeBuoy size={18} aria-hidden="true" /> },
                { label: 'Settings', to: '/settings/billing', icon: <Settings size={18} aria-hidden="true" /> },
            ],
        },
    ];

    /**
     * Signer navigation items (no section labels). Personal settings live
     * in the user-avatar menu's "Personal Settings" item (issue #2114), not
     * the main nav — so signers see only "My Policies" here.
     */
    const signerItems: NavItem[] = [{ label: 'My Policies', to: '/signer', icon: <ClipboardList size={18} aria-hidden="true" /> }];

    const drawerContent = (
        <nav className="sidebar" aria-label="Main navigation">
            {/* Sidebar header with logo */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    minHeight: 56,
                }}
            >
                <Link
                    to="/dashboard"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        textDecoration: 'none',
                        color: '#0d9488',
                        fontWeight: 700,
                        fontSize: '1.125rem',
                    }}
                >
                    <AccedoLogo size={32} />
                    Accedo
                </Link>
            </Box>

            {/* Navigation list */}
            {isAdmin ? (
                <List disablePadding sx={{ flex: 1, py: 1 }}>
                    {adminSections.map((section) => (
                        <Box key={section.label}>
                            <Typography
                                variant="caption"
                                className="sidebar-section-label"
                                sx={{
                                    display: 'block',
                                    px: 2.5,
                                    // #2168 — tighter spacing so Settings stays above the 720px fold.
                                    pt: ADMIN_SIDEBAR_LABEL_PT,
                                    pb: ADMIN_SIDEBAR_LABEL_PB,
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.07em',
                                    color: 'text.secondary',
                                    userSelect: 'none',
                                    mt: ADMIN_SIDEBAR_LABEL_MT,
                                }}
                            >
                                {section.label}
                            </Typography>
                            {section.items.map((item) => {
                                const active = isActiveRoute(item.to);
                                return (
                                    <ListItemButton
                                        key={item.to}
                                        component={Link}
                                        to={item.to}
                                        selected={active}
                                        aria-current={active ? 'page' : undefined}
                                        data-testid={`nav-${item.to.replace(/^\/+/, '').replace(/\/.*$/, '') || 'home'}`}
                                        className={`nav-button${active ? ' active' : ''}`}
                                        sx={{
                                            borderRadius: 0,
                                            px: 2.5,
                                            // #2168 — tighter vertical padding (was py:1) so the full
                                            // 9-button nav fits on a 1280×720 laptop without scrolling.
                                            py: ADMIN_SIDEBAR_BUTTON_PY,
                                            gap: 1,
                                            '&.Mui-selected': {
                                                bgcolor: 'var(--color-sidebar-active-bg, #134e4a)',
                                                color: 'var(--color-sidebar-active-text, #ccfbf1)',
                                                fontWeight: 600,
                                                borderLeft: '3px solid',
                                                borderColor: 'var(--color-sidebar-active-text, #ccfbf1)',
                                                pl: 'calc(1.25rem - 3px)',
                                                '&:hover': {
                                                    bgcolor: 'var(--color-sidebar-active-bg, #134e4a)',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 0, mr: 1, color: 'inherit' }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} slotProps={{ primary: { sx: { fontSize: '0.9375rem', color: 'inherit' } } }} />
                                    </ListItemButton>
                                );
                            })}
                        </Box>
                    ))}
                </List>
            ) : (
                <List disablePadding sx={{ flex: 1, py: 1 }}>
                    {signerItems.map((item) => {
                        const active = isActiveRoute(item.to);
                        return (
                            <ListItemButton
                                key={item.to}
                                component={Link}
                                to={item.to}
                                selected={active}
                                aria-current={active ? 'page' : undefined}
                                className={`nav-button${active ? ' active' : ''}`}
                                sx={{
                                    borderRadius: 0,
                                    px: 2.5,
                                    py: 1,
                                    gap: 1,
                                    '&.Mui-selected': {
                                        bgcolor: 'var(--color-sidebar-active-bg, #134e4a)',
                                        color: 'var(--color-sidebar-active-text, #ccfbf1)',
                                        fontWeight: 600,
                                        borderLeft: '3px solid',
                                        borderColor: 'var(--color-sidebar-active-text, #ccfbf1)',
                                        pl: 'calc(1.25rem - 3px)',
                                        '&:hover': {
                                            bgcolor: 'var(--color-sidebar-active-bg, #134e4a)',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 0, mr: 1, color: 'inherit' }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} slotProps={{ primary: { sx: { fontSize: '0.9375rem', color: 'inherit' } } }} />
                            </ListItemButton>
                        );
                    })}
                </List>
            )}
        </nav>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }} className="app app-with-sidebar">
            {/* Mobile AppBar (hidden on desktop) */}
            {!isDesktop && (
                <AppBar
                    position="fixed"
                    sx={{
                        display: { xs: 'flex', sm: 'none' },
                        bgcolor: 'var(--color-topbar-bg, #ffffff)',
                        borderBottom: 1,
                        borderColor: 'var(--color-topbar-border, #e0e0e0)',
                        color: 'text.primary',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    }}
                >
                    <Toolbar sx={{ gap: 1 }}>
                        <IconButton
                            edge="start"
                            aria-label="Open navigation"
                            onClick={handleDrawerToggle}
                            sx={{ color: 'text.secondary', minWidth: 44, minHeight: 44 }}
                        >
                            <MenuIcon size={22} />
                        </IconButton>
                        <Link
                            to="/dashboard"
                            className="mobile-logo"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                textDecoration: 'none',
                                color: '#0d9488',
                                fontWeight: 700,
                                fontSize: '1.125rem',
                                flex: 1,
                            }}
                        >
                            <AccedoLogo size={28} />
                            Accedo
                        </Link>
                        <Tooltip title="Help">
                            <IconButton
                                aria-label="Help"
                                className="icon-btn"
                                onClick={handleOpenHelp}
                                sx={{ color: 'text.secondary', minWidth: 44, minHeight: 44 }}
                            >
                                <HelpCircle size={20} aria-hidden="true" />
                            </IconButton>
                        </Tooltip>
                        <Box className="mobile-topbar-user-menu">
                            <UserMenu displayName={displayName} email={email} userType={currentUserType} onSignOut={handleSignOut} />
                        </Box>
                    </Toolbar>
                </AppBar>
            )}

            {/* Desktop permanent Drawer */}
            {isDesktop && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            bgcolor: 'var(--color-sidebar-bg)',
                            borderRight: 1,
                            borderColor: 'var(--color-sidebar-border)',
                            overflowX: 'hidden',
                        },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Mobile temporary Drawer */}
            {!isDesktop && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            bgcolor: 'var(--color-sidebar-bg)',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Main content area */}
            <Box
                component="div"
                className="main-with-header"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    ml: isDesktop ? 0 : 0,
                    mt: isDesktop ? 0 : '56px',
                }}
            >
                {/* Desktop content header with UserMenu */}
                {isDesktop && (
                    <Box
                        component="header"
                        className="content-header"
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 2.5,
                            py: 0.75,
                            bgcolor: 'background.paper',
                            borderBottom: 1,
                            borderColor: 'divider',
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                        }}
                    >
                        <Tooltip title="Help">
                            <IconButton
                                aria-label="Help"
                                className="icon-btn"
                                onClick={handleOpenHelp}
                                sx={{ color: 'text.secondary', minWidth: 44, minHeight: 44 }}
                            >
                                <HelpCircle size={20} aria-hidden="true" />
                            </IconButton>
                        </Tooltip>
                        <UserMenu displayName={displayName} email={email} userType={currentUserType} onSignOut={handleSignOut} />
                    </Box>
                )}

                <Box
                    component="main"
                    className="main-content container"
                    sx={{
                        flex: 1,
                        // #2285 — `minWidth: 0` on this column-flex item is
                        // required for descendant horizontal-scroll wrappers
                        // (e.g. the Users table's `user-table-scroll` Box)
                        // to actually overflow. Without it, a flex item
                        // defaults to `min-width: auto` and grows to fit its
                        // content's intrinsic min-width — for the Users page
                        // that's the inner Table's `min-width: 800px`, so
                        // `<Box overflowX:'auto'>` reports `scrollWidth ===
                        // clientWidth` and the inset shadow added by #2282
                        // is unbacked by real overflow. The parent
                        // `main-with-header` Box already declares
                        // `minWidth: 0`; this completes the chain.
                        minWidth: 0,
                        p: { xs: '1.5rem 1rem', md: '2rem 20px' },
                    }}
                >
                    {/*
                     * #3348 — the Suspense boundary for lazily-loaded routes
                     * (issue #2446) lives HERE, inside the content region,
                     * rather than around the whole <Routes> tree in App.tsx.
                     * The sidebar/top bar/user menu above are already
                     * committed by the time a route's lazy chunk suspends,
                     * so only this Box's content reverts to the fallback —
                     * the shell never tears down and rebuilds. Non-lazy
                     * children (most routes) never suspend, so this is a
                     * no-op for them.
                     */}
                    <Suspense fallback={<LoadingPage />}>{children}</Suspense>
                </Box>
            </Box>
        </Box>
    );
}

/** Outer component that owns the loading guard. No hooks after the early return. */
function AuthenticatedLayoutContent({ children }: AuthenticatedLayoutProps) {
    const { loading } = useCurrentUser();

    if (loading) {
        return <LoadingPage />;
    }

    // Mounted alongside CurrentUserProvider (which wraps this component) so the
    // whole authenticated tree shares a single entitlements fetch per session
    // instead of each consumer hitting the endpoint independently (#2445).
    return (
        <EntitlementsProvider>
            <AuthenticatedLayoutInner>{children}</AuthenticatedLayoutInner>
        </EntitlementsProvider>
    );
}

/**
 * Authenticated layout wrapping page content with a collapsible sidebar navigation
 * and a user avatar menu in the top-right corner of the content area.
 *
 * Uses MUI Drawer (permanent on desktop, temporary on mobile), AppBar for mobile topbar,
 * List/ListItemButton for nav links, and responsive useMediaQuery breakpoints.
 */
function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
    return (
        <CurrentUserProvider>
            <AuthenticatedLayoutContent>{children}</AuthenticatedLayoutContent>
        </CurrentUserProvider>
    );
}

export default AuthenticatedLayout;
